import { createEnv } from '@t3-oss/env-core'
import { z } from 'zod'

// Resolved at build time (pinned config + env) and injected by vite.config as
// __APP_CONFIG__. To add a value, edit src/config.ts and the schema below.
declare const __APP_CONFIG__: Record<string, string | undefined>

export const env = createEnv({
  /**
   * The prefix that client-side variables must have. This is enforced both at
   * a type-level and at runtime.
   */
  clientPrefix: 'VITE_',

  client: {
    VITE_APP_TITLE: z.string().min(1).optional(),
    // Allows EITHER an absolute URL (`https://...`, `http://...`) OR a relative
    // path starting with `/` (so dev can point at the Vite `/api` proxy). A
    // bareword like `pulsify-backend-production.up.railway.app` is rejected
    // at boot - without this refine it would silently turn into a relative
    // path against localhost:5173, every API call would 304 against the Vite
    // SPA fallback, and the only surface symptom is "login bounces straight
    // back to /admin-login".
    VITE_API_URL: z
      .string()
      .min(1)
      .refine(
        (v) => /^https?:\/\//.test(v) || v.startsWith('/'),
        'VITE_API_URL must be an absolute URL (https://...) or a path starting with `/`',
      ),
    VITE_LOG_LEVEL: z
      .enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent'])
      .optional(),
    VITE_ENV: z.enum(['development', 'staging', 'production']),
    VITE_PUBLIC_APP_URL: z.string().min(1),
    VITE_SENTRY_DSN: z.string().optional(),
    VITE_SENTRY_DISABLED: z
      .enum(['true', 'false'])
      .optional()
      .transform((val) => val === 'true'),
    VITE_SENTRY_DEBUG_LOGS: z
      .enum(['true', 'false'])
      .optional()
      .transform((val) => val === 'true'),
    VITE_SENTRY_OPERATIONS_LOGS: z
      .enum(['true', 'false'])
      .optional()
      .transform((val) => val !== 'false'),
    VITE_SENTRY_SAMPLE_RATE: z
      .string()
      .transform((val) => Number.parseFloat(val))
      .refine((val) => !Number.isNaN(val) && val >= 0.1 && val <= 1.0, {
        message: 'VITE_SENTRY_SAMPLE_RATE must be a number between 0.1 and 1.0',
      }),
    VITE_STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
    /** Attributes basemap tiles to our CARTO account; the map falls back to keyless tiles without it. */
    VITE_CARTO_BASEMAP_KEY: z.string().min(1).optional(),
    VITE_SUPABASE_URL: z.url(),
    VITE_SUPABASE_ANON_KEY: z.string().min(1),
  },

  /**
   * The resolved config object injected by vite.config (__APP_CONFIG__) holds
   * every VITE_* value, so it replaces import.meta.env as the runtime source.
   */
  runtimeEnv: __APP_CONFIG__,

  /**
   * By default, this library will feed the environment variables directly to
   * the Zod validator.
   *
   * This means that if you have an empty string for a value that is supposed
   * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
   * it as a type mismatch violation. Additionally, if you have an empty string
   * for a value that is supposed to be a string with a default value (e.g.
   * `DOMAIN=` in an ".env" file), the default value will never be applied.
   *
   * In order to solve these issues, we recommend that all new projects
   * explicitly specify this option as true.
   */
  emptyStringAsUndefined: true,
})
